<div class="page-container">
  <div class="max-content-width spaced-sections">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 class="page-title">
          {{ isSuperAdmin ? 'System- & User Management' : 'User administration' }}
        </h1>
        <p class="page-subtitle">
          {{
            isSuperAdmin
              ? 'Configure system settings and manage administrator access.'
              : 'Review registered users and manage administrator access.'
          }}
        </p>
      </div>
    </div>

    @if (!roleChecked && loading) {
      <section class="card card-padding">
        <div class="px-6 py-10 text-center text-gray-600">Checking administrator access...</div>
      </section>
    } @else if (!isAdmin) {
      <section class="card card-padding">
        <p class="badge-warning">Access restricted</p>
        <h2 class="mt-2 text-lg font-bold text-gray-900">Administrator role required</h2>
        <p class="mt-1 text-sm text-gray-600">
          Sign in with an administrator account to manage system users.
        </p>
        <div class="mt-4">
          <a routerLink="/profile" class="btn-primary">Open profile</a>
        </div>
      </section>
    } @else {
      @if (errorMessage) {
        <app-base-alert
          type="error"
          title="Administration problem"
          [message]="errorMessage"
          (closed)="errorMessage = ''"
        ></app-base-alert>
      }

      @if (isSuperAdmin) {
        <section class="card card-padding">
          <div class="grid gap-6 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)]">
            <div>
              <p class="badge-primary">System settings</p>
              <h2 class="mt-2 text-lg font-bold text-gray-900">Application domain</h2>
              <p class="mt-2 text-sm text-gray-600">
                Email links open this product domain before navigating to their target page.
              </p>
            </div>

            <form class="space-y-3" (ngSubmit)="saveApplicationDomain()">
              <div>
                <label for="applicationDomain" class="input-label">Product domain</label>
                <div class="mt-1 flex flex-col gap-2 sm:flex-row">
                  <input
                    id="applicationDomain"
                    name="applicationDomain"
                    type="url"
                    [(ngModel)]="applicationDomain"
                    placeholder="http://localhost:4200"
                    class="input-field flex-1"
                    [disabled]="domainLoading || domainSaving"
                  />
                  <button
                    type="submit"
                    class="btn-primary"
                    [disabled]="domainLoading || domainSaving || !applicationDomainChanged()"
                  >
                    {{ domainSaving ? 'Saving...' : 'Save domain' }}
                  </button>
                </div>
              </div>

              @if (domainLoading) {
                <p class="text-sm text-gray-600">Loading configured domain...</p>
              } @else if (domainErrorMessage) {
                <p class="text-sm font-medium text-red-700">{{ domainErrorMessage }}</p>
              } @else if (savedApplicationDomain) {
                <p class="text-sm text-gray-600">
                  Current domain:
                  <span class="font-semibold text-gray-900">{{ savedApplicationDomain }}</span>
                </p>
              }
            </form>
          </div>
        </section>
      }

      <section class="card card-padding">
        <div class="grid gap-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
          <div>
            <p class="badge-primary">Your system roles</p>
            <h2 class="mt-2 text-lg font-bold text-gray-900">
              {{
                currentRole === 'SUPER_ADMIN'
                  ? 'Super Administrator access'
                  : 'Administrator access'
              }}
            </h2>
            <div class="mt-4 flex flex-wrap gap-2">
              @for (role of currentRoleLabels; track role) {
                <span [class]="role === 'USER' ? 'badge-neutral' : 'badge-primary'">
                  {{ role }}
                </span>
              }
            </div>
          </div>

          <div>
            <p class="text-sm font-semibold text-gray-900">What this allows you to do</p>
            <ul class="mt-3 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
              @for (right of currentRoleRights; track right) {
                <li class="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                  {{ right }}
                </li>
              }
            </ul>
          </div>
        </div>
      </section>

      <section class="card">
        <div class="card-header">
          <div>
            <h2 class="card-title">Registered users</h2>
            <p class="card-subtitle">
              {{ users.length }} account{{ users.length === 1 ? '' : 's' }}
            </p>
          </div>

          <div class="flex items-center gap-2">
            <input
              type="search"
              [value]="filterTerm"
              (input)="updateFilter($any($event.target).value)"
              placeholder="Search users"
              class="input-field w-56"
            />
            <button type="button" class="btn-secondary" (click)="loadUsers()" [disabled]="loading">
              Refresh
            </button>
          </div>
        </div>

        @if (loading) {
          <div class="px-6 py-10 text-center text-gray-600">Loading users...</div>
        } @else if (filteredUsers.length === 0) {
          <div class="empty-state">
            <h3 class="empty-state-title">No users found</h3>
            <p class="empty-state-message">Adjust the search or refresh the list.</p>
          </div>
        } @else {
          <div class="overflow-x-auto">
            <table class="w-full min-w-[760px] text-left text-sm">
              <thead class="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th class="px-6 py-3 font-semibold">User</th>
                  <th class="px-6 py-3 font-semibold">Roles</th>
                  <th class="px-6 py-3 font-semibold">Status</th>
                  <th class="px-6 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-200">
                @for (user of filteredUsers; track user.id) {
                  <tr>
                    <td class="px-6 py-4">
                      <div class="font-semibold text-gray-900">{{ formatName(user) }}</div>
                      <div class="text-xs text-gray-600">{{ user.email }}</div>
                      @if (user.affiliation) {
                        <div class="mt-1 text-xs text-gray-500">{{ user.affiliation }}</div>
                      }
                    </td>
                    <td class="px-6 py-4">
                      <span [class]="hasAdminRole(user) ? 'badge-primary' : 'badge-neutral'">
                        {{ formatRoles(user) }}
                      </span>
                    </td>
                    <td class="px-6 py-4">
                      <div class="flex flex-wrap gap-2">
                        <span [class]="user.active ? 'badge-success' : 'badge-danger'">
                          {{ user.active ? 'Active' : 'Deactivated' }}
                        </span>
                        <span [class]="user.emailVerified ? 'badge-success' : 'badge-warning'">
                          {{ user.emailVerified ? 'Verified' : 'Unverified' }}
                        </span>
                      </div>
                    </td>
                    <td class="px-6 py-4">
                      <div class="flex justify-end gap-2">
                        @if (isSuperAdmin) {
                          @if (hasAdminRole(user)) {
                            <button
                              type="button"
                              class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-50"
                              (click)="removeAdmin(user)"
                              [disabled]="
                                !!actionUserId || isCurrentUser(user) || hasSuperAdminRole(user)
                              "
                              [title]="
                                hasSuperAdminRole(user)
                                  ? 'Super Administrator access cannot be changed here'
                                  : isCurrentUser(user)
                                    ? 'You cannot remove your own administrator access here'
                                    : 'Remove administrator role'
                              "
                            >
                              {{ actionUserId === user.id ? 'Saving...' : 'Remove admin' }}
                            </button>
                          } @else {
                            <button
                              type="button"
                              class="btn-small-primary"
                              (click)="assignAdmin(user)"
                              [disabled]="!!actionUserId || !user.active"
                            >
                              {{ actionUserId === user.id ? 'Saving...' : 'Make admin' }}
                            </button>
                          }
                        } @else {
                          <span class="btn-small-secondary cursor-default opacity-70">
                            Role managed by Super Admin
                          </span>
                        }

                        @if (user.active) {
                          <button
                            type="button"
                            class="btn-small-danger disabled:cursor-not-allowed disabled:opacity-50"
                            (click)="askDeleteUser(user)"
                            [disabled]="!canDeactivateUser(user)"
                            [title]="deactivateDisabledReason(user)"
                          >
                            Deactivate
                          </button>
                        } @else {
                          <button
                            type="button"
                            class="btn-small-primary"
                            (click)="reactivateUser(user)"
                            [disabled]="!!actionUserId"
                          >
                            {{ actionUserId === user.id ? 'Saving...' : 'Activate' }}
                          </button>
                        }
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>
    }
  </div>
</div>

<app-confirm-modal
  [open]="showDeleteConfirm"
  [title]="'Deactivate user account'"
  [message]="
    'The account for ' +
    (deleteUserCandidate ? formatName(deleteUserCandidate) : 'this user') +
    ' will be deactivated. This user will lose all research group memberships, project memberships, assignments, and related access permissions. They will be removed from collaborative workspaces and can no longer access the system until an administrator reactivates the account.'
  "
  [confirmText]="'Deactivate user'"
  [cancelText]="'Cancel'"
  [confirmVariant]="'danger'"
  [loading]="!!deleteUserCandidate && actionUserId === deleteUserCandidate.id"
  size="md"
  (openChange)="showDeleteConfirm = $event"
  (confirmed)="confirmDeleteUser()"
  (canceled)="cancelDeleteUser()"
></app-confirm-modal>
